import style from './style.module.scss';
import CalendarIcon from '../../../icons/CalendarBlank.svg?react';
import IdentificationCardIcon from '../../../icons/IdentificationCard.svg?react';
import MapTrifoldIcon from '../../../icons/MapTrifold.svg?react';
import GenderIcon from '../../../icons/GenderIntersex.svg?react';
import MapPinIcon from '../../../icons/MapPin.svg?react';
import StarIcon from '../../../icons/Star.svg?react';
import UpdatedIcon from '../../../icons/ArrowsCounterClockwise.svg?react';
import MobileIcon from '../../../icons/Mobile.svg?react';
import AppleIcon from '../../../icons/AppleLogo.svg?react';
import FacebookIcon from '../../../icons/FacebookLogo.svg?react';
import GoogleIcon from '../../../icons/GoogleLogo.svg?react';
import InvalidIcon from '../../../icons/Prohibit.svg?react';
import LockIcon from '../../../icons/LockSimple.svg?react';
import DropIcon from '../../../icons/Drop.svg?react';
import CircleIcon from '../../../icons/Circle.svg?react';
import CreditIcon from '../../../icons/CreditCard.svg?react';
import StackIcon from '../../../icons/Stack.svg?react';
import TermsIcon from '../../../icons/TermsIcon.svg?react';
import PrivacyIcon from '../../../icons/PrivacyIcon.svg?react';
import {
    AgeVerificationHistoryEntry,
    AgeVerificationStatus,
    Client,
    DeviceAppVersion,
    UserConsent,
} from '../../../stores/client';
import { UserSingleDetail } from '../user-single-detail';
import { Collapse, Spin } from 'antd';
import moment from 'moment';
import { capitalizeFirstLetter } from '../../../utils';

const { Panel } = Collapse;

type UserDetailsTabProps = {
    clientObject?: Client;
    resetPassword?: () => void;
    paymentMethod?: {
        loading: boolean;
        data: string | null;
    };
    deviceAppVersion?: {
        loading: boolean;
        data: DeviceAppVersion | null;
    };
    userConsents?: UserConsent[] | null;
    onUpdateConsentStatus?: (
        userConsentRecordId: number,
        status: string
    ) => void;
    isUpdatingConsentStatus?: boolean;
};

function formatHistoryEntry(entry: AgeVerificationHistoryEntry): string {
    const date = moment(entry.occurredAt).format('LL');
    const provinceSegment = entry.province ? ` in ${entry.province}` : '';
    const verb = entry.outcome === 'success' ? 'Verified' : 'Failed';
    const reasonSegment = entry.reason ? ` — ${entry.reason}` : '';
    return `${verb} on ${date}${provinceSegment}${reasonSegment}`;
}

export const UserDetailsTab = (props: UserDetailsTabProps) => {
    const {
        userConsents,
        onUpdateConsentStatus,
        isUpdatingConsentStatus,
    } = props;

    const ageVerificationHistory = props.clientObject?.ageVerificationHistory;

    const termsConsent = userConsents?.find(
        (consent) => consent.type === 'terms_and_conditions'
    );
    const privacyPolicyConsent = userConsents?.find(
        (consent) => consent.type === 'privacy_policy'
    );

    const handleConsentStatusChange = (consent: UserConsent) => {
        if (onUpdateConsentStatus) {
            const newStatus = 'revoked';
            onUpdateConsentStatus(consent.id, newStatus);
        }
    };

    return (
        <div className={style.details_tab}>
            <UserSingleDetail
                title={
                    <>
                        <CalendarIcon />
                        Date of birth
                    </>
                }
                info={props.clientObject?.birthday?.format('LL')}
                style={{ paddingTop: '0px' }}
            />
            {ageVerificationHistory && ageVerificationHistory.length > 0 ? (
                <Collapse
                    ghost
                    expandIconPosition="right"
                    className={style.age_verification_collapse}
                >
                    <Panel
                        header={
                            <UserSingleDetail
                                title={
                                    <>
                                        <IdentificationCardIcon />
                                        Age verification
                                    </>
                                }
                                info={
                                    props.clientObject
                                        ?.ageVerificationStatus ===
                                        AgeVerificationStatus.VERIFIED ||
                                    props.clientObject
                                        ?.ageVerificationStatus ===
                                        AgeVerificationStatus.INSUFFICIENT
                                        ? `Verified on ${moment(
                                              props.clientObject.lastVerifiedAt
                                          ).format('LL')}${
                                              props.clientObject
                                                  .verifiedInProvince
                                                  ? ` in ${props.clientObject.verifiedInProvince}`
                                                  : ''
                                          }`
                                        : 'Not verified'
                                }
                                style={{ borderBottom: 'none' }}
                            />
                        }
                        key="age_verification_history"
                    >
                        <div className={style.age_verification_history}>
                            {ageVerificationHistory.map((entry, index) => (
                                <div
                                    key={index}
                                    className={
                                        style.age_verification_history_entry
                                    }
                                >
                                    {formatHistoryEntry(entry)}
                                </div>
                            ))}
                        </div>
                    </Panel>
                </Collapse>
            ) : (
                <UserSingleDetail
                    title={
                        <>
                            <IdentificationCardIcon />
                            Age verification
                        </>
                    }
                    info={
                        props.clientObject?.ageVerificationStatus ===
                            AgeVerificationStatus.VERIFIED ||
                        props.clientObject?.ageVerificationStatus ===
                            AgeVerificationStatus.INSUFFICIENT
                            ? `Verified on ${moment(
                                  props.clientObject.lastVerifiedAt
                              ).format('LL')}${
                                  props.clientObject.verifiedInProvince
                                      ? ` in ${props.clientObject.verifiedInProvince}`
                                      : ''
                              }`
                            : 'Not verified'
                    }
                />
            )}
            <UserSingleDetail
                title={
                    <>
                        <MapTrifoldIcon />
                        Province
                    </>
                }
                info={props.clientObject?.province}
            />
            <UserSingleDetail
                title={
                    <>
                        <GenderIcon />
                        Gender
                    </>
                }
                info={props.clientObject?.gender}
            />
            <UserSingleDetail
                title={
                    <>
                        <MapPinIcon />
                        Postal Code
                    </>
                }
                info={props.clientObject?.postalCode}
            />
            <UserSingleDetail
                title={
                    <>
                        <StarIcon />
                        Created
                    </>
                }
                info={props.clientObject?.dateCreated?.format('LL')}
            />
            <UserSingleDetail
                title={
                    <>
                        <UpdatedIcon />
                        Last updated
                    </>
                }
                info={props.clientObject?.dateUpdated?.format('LLL')}
            />
            <UserSingleDetail
                title={
                    <>
                        <MobileIcon />
                        Last session
                    </>
                }
                info={props.clientObject?.lastSessionAt?.format('LLL')}
            />
            <UserSingleDetail
                title={
                    <>
                        <AppleIcon />
                        Apple ID
                    </>
                }
                info={props.clientObject?.appleId}
            />
            {props.clientObject?.facebookId && (
                <UserSingleDetail
                    title={
                        <>
                            <FacebookIcon />
                            Facebook
                        </>
                    }
                    info={props.clientObject.facebookId}
                />
            )}
            <UserSingleDetail
                title={
                    <>
                        <GoogleIcon />
                        Google ID
                    </>
                }
                info={props.clientObject?.googleId}
            />
            <UserSingleDetail
                title={
                    <>
                        <InvalidIcon />
                        Invalid logins
                    </>
                }
                info={props.clientObject?.invalidLoginCount}
            />
            <UserSingleDetail
                title={
                    <>
                        <LockIcon />
                        Locked at
                    </>
                }
                info={props.clientObject?.lockedAt?.format('LLL')}
                buttonText="Reset Password"
                buttonFunctionality={props.resetPassword}
            />
            <UserSingleDetail
                title={
                    <>
                        <DropIcon />
                        Liquid barcode ID
                    </>
                }
                info={props.clientObject?.liquidBarcodeId}
            />
            <UserSingleDetail
                title={
                    <>
                        <CircleIcon />
                        NGRP
                    </>
                }
                info={props.clientObject?.ngrpCustomerId}
            />
            <UserSingleDetail
                title={
                    <>
                        <CreditIcon />
                        Payment method
                    </>
                }
                info={
                    props.paymentMethod?.loading ? (
                        <Spin />
                    ) : (
                        props.paymentMethod?.data
                    )
                }
            />
            <UserSingleDetail
                title={
                    <>
                        <StackIcon />
                        App version
                    </>
                }
                info={
                    props.deviceAppVersion?.loading ? (
                        <Spin />
                    ) : (
                        `${props.deviceAppVersion?.data?.appVersion || '-'} ${
                            props.deviceAppVersion?.data?.os || '-'
                        }`
                    )
                }
            />

            {(termsConsent || privacyPolicyConsent) && (
                <div>
                    {termsConsent ? (
                        <UserSingleDetail
                            title={
                                <>
                                    <TermsIcon />
                                    Terms and Conditions
                                </>
                            }
                            info={
                                termsConsent.status
                                    ? `${capitalizeFirstLetter(
                                          termsConsent.status
                                      )}${
                                          termsConsent.createdAt
                                              ? ` on: ${moment(
                                                    termsConsent.createdAt
                                                ).format('LLL')}`
                                              : ''
                                      }`
                                    : 'No Status'
                            }
                            buttonText={
                                termsConsent.status === 'accepted'
                                    ? 'Revoke Consent'
                                    : 'Revoked Consent'
                            }
                            buttonFunctionality={
                                termsConsent.status === 'accepted'
                                    ? () =>
                                          handleConsentStatusChange(
                                              termsConsent
                                          )
                                    : undefined
                            }
                            buttonDisabled={
                                termsConsent.status === 'accepted'
                                    ? isUpdatingConsentStatus
                                    : true
                            }
                        />
                    ) : (
                        <UserSingleDetail
                            title={
                                <>
                                    <TermsIcon />
                                    Terms and Conditions
                                </>
                            }
                            info="Not Accepted"
                        />
                    )}
                    {privacyPolicyConsent ? (
                        <UserSingleDetail
                            title={
                                <>
                                    <PrivacyIcon />
                                    Privacy Policy
                                </>
                            }
                            info={
                                privacyPolicyConsent.status
                                    ? `${capitalizeFirstLetter(
                                          privacyPolicyConsent.status
                                      )}${
                                          privacyPolicyConsent.createdAt
                                              ? ` on: ${moment(
                                                    privacyPolicyConsent.createdAt
                                                ).format('LLL')}`
                                              : ''
                                      }`
                                    : 'No Status'
                            }
                            buttonText={
                                privacyPolicyConsent.status === 'accepted'
                                    ? 'Revoke Consent'
                                    : 'Revoked Consent'
                            }
                            buttonFunctionality={
                                privacyPolicyConsent.status === 'accepted'
                                    ? () =>
                                          handleConsentStatusChange(
                                              privacyPolicyConsent
                                          )
                                    : undefined
                            }
                            buttonDisabled={
                                privacyPolicyConsent.status === 'accepted'
                                    ? isUpdatingConsentStatus
                                    : true
                            }
                        />
                    ) : (
                        <UserSingleDetail
                            title={
                                <>
                                    <PrivacyIcon />
                                    Privacy Policy
                                </>
                            }
                            info="Not Accepted"
                        />
                    )}
                </div>
            )}
        </div>
    );
};
